import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { getEntityEndpoint } from '@/shared/api/openmetadata';
import {
  generateDisplayNameOperations,
  generateStyleOperations,
  generateTagFieldOperations,
} from '@/shared/lib/patch-operations';
import type { JsonPatchOperation } from '@/shared/types/common';

import type { TagItem, TagOption } from '@/entities/tags';

export const updateAssetTags = async (
  entityType: string,
  entityId: string,
  patchOperations: JsonPatchOperation[]
): Promise<void> => {
  try {
    const endpoint = getEntityEndpoint(entityType);
    await openMetadataApiClient.patch(`/${endpoint}/${entityId}`, patchOperations, {
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
    });
  } catch (error) {
    console.error('Failed to update asset tags:', error);
    throw error;
  }
};

const createTagFromFQN = (fqn: string, availableTags: TagOption[]): TagItem | null => {
  const tagOption = availableTags.find((t) => t.value === fqn);

  if (!tagOption) {
    return null;
  }

  const nameParts = fqn.split('.');
  const name = nameParts[nameParts.length - 1];

  return {
    tagFQN: fqn,
    name: tagOption.label || name,
    displayName: tagOption.label || '',
    description: tagOption.description || '',
    style: tagOption.style || {},
    source: 'Classification',
    labelType: 'Manual',
    state: 'Confirmed',
  };
};

export const generateTagPatchOperations = (
  oldTags: TagItem[],
  newTagFQNs: string[],
  availableTags: TagOption[]
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  const newClassificationTags: TagItem[] = newTagFQNs
    .map((fqn) => createTagFromFQN(fqn, availableTags))
    .filter((tag): tag is TagItem => tag !== null);

  const glossaryTermsFromOld = oldTags.filter((tag) => tag.source === 'Glossary');
  const newAllTags = [...glossaryTermsFromOld, ...newClassificationTags];
  const maxLength = Math.max(oldTags.length, newAllTags.length);

  for (let i = 0; i < maxLength; i++) {
    const oldTag = oldTags[i];
    const newTag = newAllTags[i];

    if (!oldTag && newTag) {
      operations.push({
        op: 'add',
        path: `/tags/${i}`,
        value: newTag,
      });
    } else if (oldTag && !newTag) {
      operations.push({
        op: 'remove',
        path: `/tags/${i}`,
      });
    } else if (oldTag && newTag) {
      const fieldOps = generateTagFieldOperations(`/tags/${i}`, newTag);
      const displayNameOps = generateDisplayNameOperations(`/tags/${i}`, oldTag.displayName, newTag.displayName);
      const styleOps = generateStyleOperations(`/tags/${i}`, oldTag.style, newTag.style);

      operations.push(...fieldOps, ...displayNameOps, ...styleOps);
    }
  }

  const removeOps = operations.filter((op) => op.op === 'remove');
  const otherOps = operations.filter((op) => op.op !== 'remove');

  removeOps.sort((a, b) => {
    const aIndex = parseInt(a.path.split('/')[2]);
    const bIndex = parseInt(b.path.split('/')[2]);
    return bIndex - aIndex;
  });

  return [...removeOps, ...otherOps];
};
